/**
 * @file src/multi/noRenderHost.js
 * @description The realm's render host when there is no 3D to host.
 *
 * A realm normally runs one `WebGLRenderer` and one rAF loop that draws every voice into its own
 * scissor rect. In the audio-first mode there is nothing to draw, so that whole apparatus is not
 * built — not built and idled, not built and skipped. This stands in its place.
 *
 * It exists because the compositor was never only a renderer: a voice also asks it for the DOM cell
 * it mounts its interface into. That part still has to happen, because the 2D interface is exactly
 * what the mode leaves the person with. The cells themselves are not ours, though, and deliberately
 * so: the realm owns them and lends them through `cellSource`, the same seam the compositor uses.
 * A host is REPLACED on every switch and the outgoing one is disposed, so a host that owned cell DOM
 * would take the voices' interfaces down with it every time the mode was toggled.
 *
 * What is left really is only the delegation plus the bookkeeping a caller expects to be able to do.
 * The voice registration is kept rather than dropped: nothing draws from it, but a caller adding and
 * removing voices should not have to know which host it is talking to, and a mismatched add/remove
 * is the kind of thing that only shows up later as a leak.
 */

/**
 * @param {{ cellSource?: { acquireCell: (index: number, total: number) => HTMLElement,
 *                         releaseCell?: (index: number) => void } | null }} opts
 */
export function createNoRenderHost({ cellSource = null } = {}) {
  const voices = new Map();
  let disposed = false;

  return {
    // No canvas and no renderer, and both are read by the voice session: a voice threads them into
    // its scene controller, which takes null to mean "own nothing, draw nothing".
    canvas: null,
    renderer: null,

    createCell(index, total) {
      return cellSource ? cellSource.acquireCell(index, total) : null;
    },

    addVoice({ voiceId, cell, controller }) {
      if (disposed || voiceId == null) return;
      voices.set(voiceId, { cell, controller });
    },

    removeVoice(voiceId) {
      voices.delete(voiceId);
    },

    /** How many voices are registered. Nothing draws them; this is for tests and teardown checks. */
    get voiceCount() {
      return voices.size;
    },

    setPerformanceMode() {
      // The mode is why this host exists. There is no cheaper state for it to move to.
    },

    dispose() {
      disposed = true;
      voices.clear();
    },
  };
}
